// pms-core / domain — the RATE GENERATOR (R14, PMS-RATE-MODEL-BRIEF §13.3).
//
// A pure function `(parameterSet, dateRange) → Rate[]`. No IO, no clock, no
// Supabase, no UI — testable the day the parameter set exists, which is the
// whole reason the set comes first.
//
// The arithmetic is §2's, unchanged:
//
//   CH [type] = base + markup[season][type]
//   B&B[type] = base + markup[season][type] + occ[type] × breakfast
//   DP [type] = base + markup[season][type] + occ[type] × (breakfast + dinner)
//
// where `occ[type]` is `pms_room_type.maxOccupancy` — the model keys off a
// column that already exists.
//
// ⚠ THIS GENERATOR PROPOSES; IT DOES NOT OWN (R4). `pms_rate` is the
// materialised, hand-editable master, and the operator adjusts days after a
// run. The consequence that must never be lost: **regeneration may not silently
// overwrite a hand-adjusted day.** That guard belongs to whatever WRITES this
// output, and this module deliberately returns a value rather than performing a
// write, so the guard cannot be forgotten by accident here.

import type { IsoDate, Rate, RatePlan, RoomType, Cents } from './types.js';
import type { RateParameterSet, RateSeason, LengthOfStayTier, MealPrices } from './rateParameters.js';
import { RATE_SEASONS, seasonOn } from './rateParameters.js';

// ── Date walking ─────────────────────────────────────────────────────────────

/** Inclusive list of civil dates. Pure string arithmetic through UTC, which is
 *  safe here because these are date-only values with no time component — the
 *  Europe/Paris civil date is decided by the caller, never re-derived here. */
export function datesBetween(from: IsoDate, to: IsoDate): IsoDate[] {
  const out: IsoDate[] = [];
  for (let d = new Date(`${from}T00:00:00Z`); ; d = new Date(d.getTime() + 86_400_000)) {
    const iso = d.toISOString().slice(0, 10);
    if (iso > to) break;
    out.push(iso);
  }
  return out;
}

// ── The room-rate half ───────────────────────────────────────────────────────

/** Accommodation only, before any meal is added — §2's `CH` line. */
export function roomRate(set: RateParameterSet, season: RateSeason, roomTypeId: number): Cents | null {
  const rt = set.roomTypes[roomTypeId];
  if (rt === undefined) return null;
  return set.seasons[season].base + rt.markup[season];
}

/** What the meals on a board cost, per night, for a whole room of `occupancy`.
 *  Single occupancy is the same line with `occ = 1` — a lone guest pays the
 *  room markup but only one breakfast (§2). */
export function mealComponent(board: RatePlan['board'], occupancy: number, meals: MealPrices): Cents {
  switch (board) {
    case 'none':
      return 0;
    case 'bb':
      return occupancy * meals.breakfast;
    case 'hb':
      return occupancy * (meals.breakfast + meals.dinner);
  }
}

// ── The generator ────────────────────────────────────────────────────────────

export interface GenerateRatesInput {
  set: RateParameterSet;
  /** The plans to generate for. A plan's `board` is the only thing consulted —
   *  which plans exist, and which are distributed, is not this function's
   *  business. */
  plans: RatePlan[];
  roomTypes: RoomType[];
  from: IsoDate;
  to: IsoDate;
}

export interface GenerateRatesResult {
  rates: Rate[];
  /** Dates the season calendar does not cover. NOT an error and NOT skipped
   *  silently: a calendar re-cut for a new year legitimately stops before the
   *  next is authored, and the caller must be able to say « the grid stops on
   *  the 14th » rather than discovering a hole later. */
  uncoveredDates: IsoDate[];
  /** Room types with no entry in the set. Same reasoning: reported, never
   *  guessed at. */
  unpricedRoomTypeIds: number[];
}

/**
 * Generate the OTA-facing grid for a date range.
 *
 * Deterministic: same set + same dates + same reference data ⇒ same output,
 * which is what makes two sets' grids comparable (§13.2 property 3). Rates are
 * emitted in a stable order (date, plan, room type) so a diff of two runs is a
 * diff of prices, not of ordering.
 *
 * The length-of-stay ladder is deliberately NOT applied here: it is a
 * direct-engine function (§8.3) and no length-of-stay row is ever published to
 * an OTA. See `applyLengthOfStayLadder`.
 */
export function generateRates(input: GenerateRatesInput): GenerateRatesResult {
  const { set, plans, roomTypes, from, to } = input;

  const rates: Rate[] = [];
  const uncoveredDates: IsoDate[] = [];
  const unpriced = new Set<number>();

  const priced = roomTypes.filter((rt) => {
    const known = set.roomTypes[rt.id] !== undefined;
    if (!known) unpriced.add(rt.id);
    return known;
  });

  for (const date of datesBetween(from, to)) {
    const season = seasonOn(set, date);
    if (season === null) {
      uncoveredDates.push(date);
      continue;
    }
    const { minStay } = set.seasons[season];

    for (const plan of plans) {
      for (const rt of priced) {
        const room = roomRate(set, season, rt.id);
        // Cannot be null — `priced` filtered on exactly this — but the compiler
        // does not know that and a bare `!` would outlive the reason.
        if (room === null) continue;

        rates.push({
          ratePlanId: plan.id,
          roomTypeId: rt.id,
          date,
          priceCents: room + mealComponent(plan.board, rt.maxOccupancy, set.mealsBooking),
          minStay,
          // A generated grid is never closed: closure is an operational act
          // (a stop sell, a season the hotel does not open), not a price.
          closed: false,
        });
      }
    }
  }

  return { rates, uncoveredDates, unpricedRoomTypeIds: [...unpriced] };
}

// ── The direct ladder ────────────────────────────────────────────────────────

/** The tier that applies to a stay of `nights`, or null below the first rung.
 *
 *  Takes the LADDER rather than a whole `RateParameterSet`: the season calendar
 *  and the rate profile hold the same ladder under a different roof
 *  (`gridGenerator.ts`), and widening the parameter is what lets both paths call
 *  one implementation instead of the second one drifting. Every existing caller
 *  passes a set, which still satisfies this shape structurally. */
export function tierFor(
  set: { lengthOfStayTiers: readonly LengthOfStayTier[] },
  nights: number,
): LengthOfStayTier | null {
  const sorted = [...set.lengthOfStayTiers].sort((a, b) => a.minNights - b.minNights);
  let found: LengthOfStayTier | null = null;
  for (const t of sorted) if (nights >= t.minNights) found = t;
  return found;
}

/**
 * The DIRECT per-night price — §11.6b, with the stay and the meals discounted
 * independently (R2):
 *
 *   quote = room × (1 − discountStay) + meals × (1 − discountMeals)
 *
 * Two things here invert what §7 originally said, and both are deliberate:
 * the meal prices are the DIRECT pair, and the two discounts are separate. A
 * direct booking is therefore always at least 7 % under the OTA rate, which is
 * the commercial point of the whole ladder.
 *
 * Rounding is to the cent, once, at the end — never per component, which would
 * let a rounding crumb ride on every line.
 */
export function directNightlyRate(
  set: RateParameterSet,
  season: RateSeason,
  roomType: RoomType,
  board: RatePlan['board'],
  nights: number,
): Cents | null {
  const room = roomRate(set, season, roomType.id);
  if (room === null) return null;

  const meals = mealComponent(board, roomType.maxOccupancy, set.mealsDirect);
  const tier = tierFor(set, nights);
  const stayOff = tier?.discountStay ?? 0;
  const mealsOff = tier?.discountMeals ?? 0;

  return Math.round(room * (1 - stayOff) + meals * (1 - mealsOff));
}

// ── Validation ───────────────────────────────────────────────────────────────

/**
 * Structural problems in a set, as human-readable French strings.
 *
 * Deliberately NOT a boolean and NOT an exception: a set with a gap in its
 * calendar is a legitimate work-in-progress that the operator is mid-way
 * through authoring, and the right response is to show what is missing, not to
 * refuse the object.
 */
export function validateRateParameterSet(set: RateParameterSet): string[] {
  const problems: string[] = [];

  if (set.name.trim() === '') problems.push('Le jeu de paramètres doit porter un nom.');

  for (const s of RATE_SEASONS) {
    if (set.seasons[s] === undefined) {
      problems.push(`Saison « ${s} » absente du jeu de paramètres.`);
      continue;
    }
    if (set.seasons[s].minStay < 1) {
      problems.push(`Saison « ${s} » : la durée minimale doit être d'au moins 1 nuit.`);
    }
  }

  for (const [id, rt] of Object.entries(set.roomTypes)) {
    for (const s of RATE_SEASONS) {
      if (rt.markup[s] === undefined) {
        problems.push(`Catégorie ${id} : supplément manquant pour la saison « ${s} ».`);
      }
    }
  }

  // Overlap is permitted (later wins, so a correction can be stapled on) but
  // reported, because an unintended overlap and a deliberate one look identical
  // in the data and only the operator can tell them apart.
  const periods = [...set.seasonCalendar].sort((a, b) => a.from.localeCompare(b.from));
  periods.forEach((cur, i) => {
    const prev = periods[i - 1];
    if (prev !== undefined && cur.from <= prev.to) {
      problems.push(`Chevauchement du calendrier : ${cur.from} est déjà couvert par ${prev.from}–${prev.to}.`);
    }
  });
  for (const p of set.seasonCalendar) {
    if (p.to < p.from) problems.push(`Période invalide : ${p.from}–${p.to} se termine avant de commencer.`);
  }

  const tiers = [...set.lengthOfStayTiers].sort((a, b) => a.minNights - b.minNights);
  for (const t of tiers) {
    for (const [label, v] of [['séjour', t.discountStay], ['repas', t.discountMeals]] as const) {
      if (v < 0 || v >= 1) {
        problems.push(`Palier ${t.minNights} nuits : remise ${label} hors bornes (${v}).`);
      }
    }
  }

  return problems;
}

/**
 * R11's consistency check: a posted « à partir de » must actually be reachable.
 *
 * This is the mechanism that has to exist by the Winter rate change (R11) —
 * `apps/site` posts hand-authored numbers with no relationship to the rate
 * model, so whether they agree with it on any given day is luck. The check is a
 * validation OVER A SET, which is precisely why it became cheap once R14
 * existed.
 *
 * Compares against the DEEPEST direct price (the longest ladder rung, meals
 * excluded — a posted room price is a room price), because that is the cheapest
 * a guest can actually buy the room for.
 */
export function checkPostedPrices(set: RateParameterSet, roomTypes: RoomType[]): string[] {
  const problems: string[] = [];
  const deepest = [...set.lengthOfStayTiers].sort((a, b) => b.minNights - a.minNights)[0];

  for (const rt of roomTypes) {
    const params = set.roomTypes[rt.id];
    if (params?.minPosted == null) continue;

    let cheapest: Cents | null = null;
    for (const s of RATE_SEASONS) {
      const room = roomRate(set, s, rt.id);
      if (room === null) continue;
      const withLadder = Math.round(room * (1 - (deepest?.discountStay ?? 0)));
      if (cheapest === null || withLadder < cheapest) cheapest = withLadder;
    }
    if (cheapest !== null && params.minPosted < cheapest) {
      problems.push(
        `${rt.labelFr} : prix affiché ${(params.minPosted / 100).toFixed(2)} € — ` +
          `aucun parcours de réservation ne descend sous ${(cheapest / 100).toFixed(2)} €.`,
      );
    }
  }

  return problems;
}
